'use client';

import { cn } from '@bells/core/lib/utils';
import { blogReadTime } from '@bells/i18n';
import type { BlogPostFrontendResponse } from '@bells/schema/models/blog';
import { Calendar, CameraOff, Clock, User } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { AspectRatio } from './aspect-ratio';
import { CalendarFormat } from './date-formatter';
import { Skeleton } from './skeleton';

export const hoursAndDaysStrings = {
  sameDay: 'HH:mm',
  lastDay: 'DD.MM.YYYY HH:mm',
  lastWeek: 'DD.MM.YYYY HH:mm',
  sameElse: 'DD.MM.YYYY HH:mm',
};

export const BlogItem = ({
  item,
  className,
}: {
  item: BlogPostFrontendResponse;
  className?: string;
}) => {
  const url =
    item.language == 'de'
      ? `/blog/${item.category.slug}/${item.slug}`
      : `/${item.language}/blog/${item.category.slug}/${item.slug}`;
  return (
    <article
      className={cn(
        'group/parent flex h-full min-h-0 w-full min-w-0 flex-col gap-2 overflow-hidden',
        className
      )}
    >
      <Link href={url} prefetch={false} className='flex min-h-0 min-w-0'>
        <AspectRatio
          ratio={4 / 3}
          className='group relative overflow-hidden rounded-xl'
        >
          <figure className='relative h-full w-full duration-500 group-hover:scale-105'>
            <Skeleton
              className={cn('absolute left-0 top-0 h-full w-full')}
            ></Skeleton>

            {item.image?.cover && (
              <Image
                src={item.image?.thumbnail}
                alt={item.title}
                fill
                unoptimized
                loading={'lazy'}
                placeholder={item.blurThumbnail ? 'blur' : undefined}
                blurDataURL={item.blurThumbnail ?? undefined}
                className={cn('h-full w-full object-cover transition-all')}
              ></Image>
            )}

            {!item.image?.cover && (
              <div className='absolute left-0 top-0 flex h-full w-full items-center justify-center bg-muted text-muted-foreground'>
                <CameraOff></CameraOff>
              </div>
            )}
          </figure>
        </AspectRatio>
      </Link>

      <div className='mt-1 flex min-h-0 w-full min-w-0 flex-col flex-wrap gap-2 pt-1'>
        <div className='flex min-h-0 min-w-0 flex-wrap items-center gap-2 text-sm text-muted-foreground'>
          <div>
            <Clock className='mr-1 inline-block h-4 w-4'></Clock>
            {blogReadTime(
              { minutes: item.document?.readingTime ?? 0 },
              { locale: item.language }
            )}
          </div>

          <div>
            <User className='mr-1 inline-block h-4 w-4'></User>
            {item.user.name}
          </div>

          <div>
            <Calendar className='mr-1 inline-block h-4 w-4'></Calendar>
            <CalendarFormat
              date={item.updated_at ?? ''}
              locale={item.language}
            ></CalendarFormat>
          </div>
        </div>
        <Link
          prefetch={false}
          className='m-0 line-clamp-3 w-full break-words font-sans text-xl font-medium hover:underline xl:line-clamp-2 xl:text-2xl'
          href={url}
        >
          {item.title}
        </Link>
        <div className='text-paragraph line-clamp-3 w-full break-words text-base'>
          {item.description}
        </div>
      </div>
    </article>
  );
};
